import type { AtlasRecord, MapDefinition } from '@grs-atlas/shared';

interface RecordsTableProps {
  map: MapDefinition;
  records: AtlasRecord[];
  onEdit: (record: AtlasRecord) => void;
  onSelect: (record: AtlasRecord) => void;
  canEdit: boolean;
}

export function RecordsTable({ map, records, onEdit, onSelect, canEdit }: RecordsTableProps) {
  const columns = map.tableColumns.length
    ? map.tableColumns
    : ['name', ...map.fields.slice(0, 4).map((f) => f.key)];

  const labelFor = (key: string) => {
    if (key === 'name') return 'Name';
    if (key === 'stateCodes') return 'States';
    if (key === 'status') return 'Status';
    if (key === 'updatedAt') return 'Updated';
    return map.fields.find((f) => f.key === key)?.label ?? key;
  };

  const cellValue = (record: AtlasRecord, key: string) => {
    if (key === 'name') return record.name;
    if (key === 'stateCodes') return record.stateCodes.join(', ');
    if (key === 'status') return record.status;
    if (key === 'updatedAt') return new Date(record.updatedAt).toLocaleString();
    if (key === 'tags') return record.tags.join(', ');
    const v = record.fields[key];
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return v == null ? '' : String(v);
  };

  if (!records.length) {
    return <div className="empty">No records match the current filters.</div>;
  }

  return (
    <div style={{ overflow: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} scope="col">
                {labelFor(col)}
              </th>
            ))}
            {canEdit && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.recordId} style={{ cursor: 'pointer' }} onClick={() => onSelect(r)}>
              {columns.map((col) => (
                <td key={col}>{cellValue(r, col)}</td>
              ))}
              {canEdit && (
                <td>
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(r);
                    }}
                  >
                    Edit
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
